import React, { useState, useContext, useEffect } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import AuthContext from "../AuthContext";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import { ClipLoader } from "react-spinners"; // Import a spinner component

const TodaySales = () => {
  const { isLoggedIn } = useContext(AuthContext);
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state

  useEffect(() => {
    if (!isLoggedIn) {
      console.log("User is not logged in. Redirecting to login page.");
      navigate("/login");
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true); // Set loading to true before fetching data
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const response = await axiosInstance.get(
          `/api/orders/allByDate?restaurantId=${user.restaurantId}&orderDate=${selectedDate}&timezone=${user.restaurantTimezone}`
        );  
        setOrders(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false); // Set loading to false after fetching data
      }
    };

    fetchOrders();
  }, [selectedDate]);

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

   
  // Calculate total sales, revenue, and canceled orders for the selected date
  const totalSales = orders.filter(
    (order) => order.status === "paid"
  ).length;
  const totalRevenue = orders
    .reduce(
      (acc, order) => acc + (order.status === "paid" ? order.total : 0),
      0
    )
    .toFixed(2);
  const totalCanceled = orders.filter(
    (order) => order.status === "canceled"
  ).length;

  return (
    <div className="min-h-screen bg-white p-6">
      <h1 className="text-4xl font-bold text-gray-800 text-center mb-8">
        Today's Sales
      </h1>

      {/* Sales Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <div className="bg-green-500 text-white p-4 rounded-lg shadow-lg text-center">
          <h2 className="text-2xl font-semibold">Total Sales</h2>
          <p className="text-4xl">{totalSales}</p>
        </div>
        <div className="bg-blue-500 text-white p-4 rounded-lg shadow-lg text-center">
          <h2 className="text-2xl font-semibold">Total Revenue</h2>
          <p className="text-4xl">${totalRevenue}</p>
        </div>
        <div className="bg-red-500 text-white p-4 rounded-lg shadow-lg text-center">
          <h2 className="text-2xl font-semibold">Total Canceled</h2>
          <p className="text-4xl">{totalCanceled}</p>
        </div>
      </div>

      {/* Calendar */}
      <div className="flex justify-center items-center mb-8">
        <label
          htmlFor="date"
          className="mr-4 flex items-center text-lg font-medium text-gray-700"
        >
          <FaCalendarAlt className="mr-2" />{" "}
          {/* Optional icon for visual enhancement */}
          Select Date:
        </label>
        <input
          type="date"
          value={selectedDate}
          onChange={handleDateChange}
          className="border border-gray-300 rounded-lg px-4 py-2 text-gray-800 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition duration-200 ease-in-out hover:border-blue-400"
        />
      </div>

      {/* Loading Spinner */}
      {loading ? (
        <div className="flex justify-center items-center my-10">
          <ClipLoader size={50} color={"#36d7b7"} loading={loading} />
        </div>
      ) : (
        <>
          {/* Paid Orders Section */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">
              Paid Orders
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {orders
                .filter((order) => order.status === "paid")
                .map((order) => (
                  <OrderCard key={order._id} order={order} />
                ))}
            </div>
          </section>

          {/* Canceled Orders Section */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">
              Canceled Orders
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {orders
                .filter((order) => order.status === "canceled")
                .map((order) => (
                  <OrderCard key={order.id} order={order} isCanceled />
                ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

// OrderCard component to display each order
const OrderCard = ({ order, isCanceled = false }) => {
  return (
    <div className="bg-white p-4 rounded-lg shadow-lg">
      <div className="flex justify-between mb-2">
        <h3 className="text-lg font-bold">Table {order.tableNumber}</h3>
        <span className="text-gray-500">
          {new Date(order.orderDate).toLocaleTimeString()}
        </span>
      </div>
      <p className="text-gray-700 mb-2">
        {order.customerDetails.name} - {order.customerDetails.phone} - {order.customerDetails.email}
      </p>
      <ul className="mb-2">
        {order.foodDetails.map((item, index) => (
          <li key={index} className="text-gray-600">
            {item.quantity}x {item.name}
          </li>
        ))}
      </ul>
      <p className="font-semibold mb-4">Total: ${order.total.toFixed(2)}</p>

      {isCanceled ? (
        <p className="text-red-500">Canceled: {order.cancellationReason}</p>
      ) : null}
    </div>
  );
};

export default TodaySales;
